/**
 * PATH: packages/email-hub/src/feature/components/email-reader/ReaderHeader.tsx
 * FILE_ID: EMAILHUB-EMAILREADER-HEADER-001
 * PURPOSE: Render reader identity, zoom, find, and action controls.
 */
import {
  Archive,
  ArrowLeft,
  CalendarPlus,
  Forward,
  Link2,
  Reply,
  ReplyAll,
  Search,
  Star,
  Trash2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import { cn } from '../../../_internal/lib/utils'
import { PingTeammateButton } from '../PingTeammateButton'
import { SnoozeMenuButton } from '../SnoozeMenuButton'
import { WhoCanSeeButton } from '../WhoCanSeeButton'
import type { ReaderToolbarActionId } from './types'
import type { EmailReaderController } from './useEmailReaderController'
import { ZOOM_MAX, ZOOM_MIN } from './useReaderZoom'

interface ReaderHeaderProps {
  controller: EmailReaderController
}

export function ReaderHeader({ controller }: ReaderHeaderProps) {
  const {
    email,
    isNarrow,
    onBack,
    backLabel,
    bodyZoom,
    zoomIn,
    zoomOut,
    resetZoom,
    find,
    pingOpen,
    setPingOpen,
    refetch,
    sortedToolbarActions,
    trackToolbarAction,
  } = controller
  if (!email) return null

  const track = (id: ReaderToolbarActionId) => trackToolbarAction(id)
  const actionButton = (id: ReaderToolbarActionId) => {
    if (id === 'snooze') {
      return (
        <SnoozeMenuButton
          key={id}
          onSnooze={(iso) => {
            track(id)
            controller.snooze(iso)
          }}
        />
      )
    }
    const run = () => {
      track(id)
      if (id === 'reply' || id === 'reply_all' || id === 'forward') {
        controller.openCompose(id)
      } else if (id === 'schedule') controller.schedule()
      else if (id === 'link') controller.setShowLinkModal(true)
      else if (id === 'archive') controller.archiveEmail()
      else if (id === 'trash') controller.trashEmail()
      else if (id === 'star') controller.toggleEmailStar()
    }
    if (id === 'reply') {
      return (
        <button
          key={id}
          onClick={run}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          <Reply className="h-3.5 w-3.5" /> Reply
        </button>
      )
    }
    if (id === 'reply_all') {
      return (
        <button
          key={id}
          onClick={run}
          title="Reply to sender + all original recipients"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-border rounded-md hover:bg-accent"
        >
          <ReplyAll className="h-3.5 w-3.5" /> Reply All
        </button>
      )
    }
    if (id === 'forward') {
      return (
        <button
          key={id}
          onClick={run}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-border rounded-md hover:bg-accent"
        >
          <Forward className="h-3.5 w-3.5" /> Forward
        </button>
      )
    }
    if (id === 'schedule') {
      return (
        <button
          key={id}
          onClick={run}
          title="Create a calendar invite from this email (prefills attendees, subject, and notes)"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-border rounded-md hover:bg-accent"
        >
          <CalendarPlus className="h-3.5 w-3.5" /> Schedule
        </button>
      )
    }
    if (id === 'link') {
      return (
        <button
          key={id}
          onClick={run}
          title="Link to Job"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-border rounded-md hover:bg-accent"
        >
          <Link2 className="h-3.5 w-3.5" /> Link
        </button>
      )
    }
    const Icon = id === 'archive' ? Archive : id === 'trash' ? Trash2 : Star
    const title =
      id === 'archive'
        ? 'Archive'
        : id === 'trash'
          ? 'Delete'
          : email.is_starred
            ? 'Unstar'
            : 'Star'
    const ariaLabel =
      id === 'star'
        ? email.is_starred
          ? 'Remove star'
          : 'Star email'
        : `${title} email`
    return (
      <button
        key={id}
        onClick={run}
        className={cn(
          'p-2 rounded-md hover:bg-accent',
          id === 'trash' && 'text-destructive',
          id === 'star' && email.is_starred && 'text-amber-500',
        )}
        aria-label={ariaLabel}
        title={title}
      >
        <Icon
          className={cn(
            'h-4 w-4',
            id === 'archive' && 'text-muted-foreground',
            id === 'star' && email.is_starred && 'fill-current',
          )}
        />
      </button>
    )
  }

  return (
    <div className="px-4 py-2.5 border-b border-border flex items-center justify-between bg-muted/10">
      <div className="flex items-center gap-3 min-w-0">
        {!isNarrow && (
          <button
            type="button"
            onClick={onBack}
            title={backLabel}
            aria-label={backLabel}
            className="p-1.5 rounded-md hover:bg-accent"
          >
            <ArrowLeft className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
        <h1
          className="text-base font-semibold text-foreground truncate max-w-xl"
          title={email.subject || '(no subject)'}
        >
          {email.subject || '(no subject)'}
        </h1>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <div
          role="group"
          aria-label="Zoom email body"
          className="flex items-center rounded-md border border-border/60 overflow-hidden mr-1"
        >
          <button
            type="button"
            onClick={zoomOut}
            disabled={bodyZoom <= ZOOM_MIN}
            title="Zoom out (⌘/Ctrl-scroll)"
            aria-label="Zoom out email body"
            className="p-1.5 hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ZoomOut className="h-4 w-4 text-muted-foreground" />
          </button>
          <button
            type="button"
            onClick={resetZoom}
            title="Reset zoom to 100%"
            aria-label="Reset email zoom to 100 percent"
            className="px-1.5 min-w-[3rem] text-xs tabular-nums text-muted-foreground hover:bg-accent"
          >
            {Math.round(bodyZoom * 100)}%
          </button>
          <button
            type="button"
            onClick={zoomIn}
            disabled={bodyZoom >= ZOOM_MAX}
            title="Zoom in (⌘/Ctrl-scroll)"
            aria-label="Zoom in email body"
            className="p-1.5 hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ZoomIn className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => find.open()}
          title="Find in thread (⌘F / Ctrl+F)"
          aria-label="Find in thread"
          data-testid="reader-find-button"
          className="p-2 rounded-md hover:bg-accent"
        >
          <Search className="h-4 w-4 text-muted-foreground" />
        </button>
        <PingTeammateButton
          emailId={email.id}
          onPinged={() => refetch()}
          open={pingOpen}
          onOpenChange={setPingOpen}
        />
        <WhoCanSeeButton emailId={email.id} />
        {sortedToolbarActions.map(({ id }) => actionButton(id))}
      </div>
    </div>
  )
}
